import { useCallback, useEffect, useState } from 'react';
import { postUserAuditLog } from '@/apis/core/UserAuditLog/post/postAuditLog';
import { UserWorkspace } from '@/apis/core/workspace/get/getUserWorkspace';
import { AuthContextProps } from '@/contexts/AuthContext';
import {
  RecentActivityActionFilter,
  RecentActivityDisplayItem,
  RecentActivityEntityFilter,
  mapAuditLogsToDisplayItems,
  parseAuditResponse,
  processAuditLogsForRecentActivity,
} from '@/utils/userAuditLog/recentActivity';

const DEFAULT_LOOKBACK_DAYS = 30;
export const RECENT_ACTIVITY_PAGE_SIZE = 6;
/** Raw logs fetched per UI page (filtering reduces visible rows). */
export const RECENT_ACTIVITY_API_BATCH = 40;

export type UseUserRecentAuditActivityParams = {
  enabled: boolean;
  getAccessToken: AuthContextProps['getAccessToken'];
  userId: string;
  workspaces: UserWorkspace[];
  page: number;
  actionFilter: RecentActivityActionFilter;
  entityFilter: RecentActivityEntityFilter;
};

export const useUserRecentAuditActivity = ({
  enabled,
  getAccessToken,
  userId,
  workspaces,
  page,
  actionFilter,
  entityFilter,
}: UseUserRecentAuditActivityParams) => {
  const [items, setItems] = useState<RecentActivityDisplayItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const fetchPage = useCallback(async () => {
    if (!enabled || !userId) {
      setItems([]);
      setHasMore(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - DEFAULT_LOOKBACK_DAYS);

      const offset = String(page * RECENT_ACTIVITY_API_BATCH);
      const response = await postUserAuditLog({
        getAccessToken,
        body: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          results: String(RECENT_ACTIVITY_API_BATCH),
          offset,
          users: [userId],
        },
      });

      const responses = parseAuditResponse(response);
      const rawLogs = responses.flatMap((res) => res.logs ?? []);
      const processed = processAuditLogsForRecentActivity(
        rawLogs,
        actionFilter,
        entityFilter,
      );
      const pageItems = processed.slice(0, RECENT_ACTIVITY_PAGE_SIZE);

      setItems(mapAuditLogsToDisplayItems(pageItems, workspaces));
      setHasMore(rawLogs.length >= RECENT_ACTIVITY_API_BATCH);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load recent activity';
      setError(message);
      setItems([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [
    actionFilter,
    enabled,
    entityFilter,
    getAccessToken,
    page,
    userId,
    workspaces,
  ]);

  useEffect(() => {
    void fetchPage();
  }, [fetchPage]);

  return {
    items,
    isLoading,
    error,
    hasMore,
    refetch: fetchPage,
  };
};
